export const name = "KLL_QUANTILES.MERGE_INT64";
export const summary = `Merges \`INT64\`-initialized KLL sketches into a new sketch, and then gets the quantiles from the new sketch.`;
export const reference = `
## KLL_QUANTILES.MERGE_INT64

Merges \`INT64\`-initialized KLL sketches into a new sketch, and then gets the quantiles from the new sketch.

\`\`\`
KLL_QUANTILES.MERGE_INT64(sketch, num_quantiles)
\`\`\`

### Description

Takes KLL sketches as \`BYTES\` and merges them into a new sketch, then returns the quantiles that divide the input into\`num_quantiles\` equal-sized groups, along with the minimum and maximum values of the input. The output is an \`ARRAY\` containing the exact minimum value from the input data that you used to initialize the sketches, each approximate quantile, and the exact maximum value from the initial input data. This is an aggregate function.

If the merged sketches were initialized with different precisions, the precision is downgraded to the lowest precision involved in the merge — except if the aggregations are small enough to still capture the input exactly — then the mergee's precision is maintained.

Returns an error if the underlying type of one or more input sketches isn't compatible with type \`INT64\`.

Returns an error if the input isn't a valid KLL quantiles sketch.


### Supported Argument Types

- \`sketch\`: \`BYTES\` KLL sketch initialized on \`INT64\` data type
- \`num_quantiles\`: A positive \`INT64\` value that represents the number of roughly equal-sized groups to divide the merged sketches into. The maximum value is 100,000.


### Return Type

\`ARRAY<INT64>\`


### Example

The following query initializes two KLL sketches from five rows of data each. Then it merges these two sketches and returns an \`ARRAY\` containing the minimum, median, and maximum values in the input sketches.



\`\`\`
SELECT KLL_QUANTILES.MERGE_INT64(kll_sketch, 2) AS halves
FROM (SELECT KLL_QUANTILES.INIT_INT64(x, 1000) AS kll_sketch
      FROM (SELECT 1 AS x UNION ALL
            SELECT 2 AS x UNION ALL
            SELECT 3 AS x UNION ALL
            SELECT 4 AS x UNION ALL
            SELECT 5)
      UNION ALL
      SELECT KLL_QUANTILES.INIT_INT64(x, 1000) AS kll_sketch
      FROM (SELECT 6 AS x UNION ALL
            SELECT 7 AS x UNION ALL
            SELECT 8 AS x UNION ALL
            SELECT 9 AS x UNION ALL
            SELECT 10 AS x));

/*----------*
 | halves |
 +----------+
 | [1,5,10] |
 *----------*/
\`\`\`


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/kll_functions
`;
